/**
 * adFrequency.js — client-side frequency throttle for Monetag auto-serve zones.
 *
 * WHY: Monetag's `tag.min.js` is a multi-format auto-serve SDK that fires
 * ads on every page navigation + whenever it feels like it. Loading it
 * globally in index.html (what we had before) means a vignette on login,
 * dashboard, class pick, every floor transition, etc. — death by a
 * thousand ads, kills retention.
 *
 * WHAT: This module controls exactly when the SDK is injected into / removed
 * from the DOM so we get a predictable cadence that respects the player:
 *
 *   1. 90s GRACE PERIOD at the start of each gameplay session — zero ads,
 *      so the first impression is clean and the player gets hooked.
 *   2. Then an AD WINDOW of 45s during which the SDK is active and may
 *      show one vignette / in-page push.
 *   3. Then a QUIET PERIOD of ~4 minutes where the SDK is removed from
 *      the DOM entirely — Monetag physically cannot serve ads during this.
 *   4. Repeat (window → quiet → window → quiet ...).
 *
 * Result: at most ~1 auto-serve ad every ~5 minutes, instead of ~1 per
 * page navigation. User-initiated rewarded ads (revive, gold doubler) are
 * unaffected — they go through showRewarded() in h5ads.js.
 *
 * This is a BACKSTOP. The *proper* fix is to also set frequency caps in
 * your Monetag publisher dashboard: Sites → Zone → Settings → "Frequency
 * capping" / "Impressions per user per hour".
 */

const SDK_URL = "https://quge5.com/88/tag.min.js";
const ZONE = "289835";
const SCRIPT_ID = "monetag-tag-min";

// ---- Cadence (tune these to taste) --------------------------------------
const GRACE_MS   = 180_000;   // No ads for the first 3 min of each session
const WINDOW_MS  = 30_000;    // SDK active for 30s (long enough for one ad)
const QUIET_MS   = 600_000;   // SDK removed for 10 minutes between windows
// -------------------------------------------------------------------------

let _running = false;
let _paused = false;
let _timers = [];
let _cycleCount = 0;

function _log(ev, data = {}) {
  // eslint-disable-next-line no-console
  console.info(`[ad-frequency] ${ev}`, data);
}

/** Insert the Monetag SDK script tag into <head> if not already present. */
function _injectSdk() {
  if (document.getElementById(SCRIPT_ID)) return false; // already loaded
  const s = document.createElement("script");
  s.id = SCRIPT_ID;
  s.src = SDK_URL;
  s.async = true;
  s.setAttribute("data-zone", ZONE);
  s.setAttribute("data-cfasync", "false");
  (document.head || document.documentElement).appendChild(s);
  _log("sdk_injected", { cycle: _cycleCount });
  return true;
}

/**
 * Remove the Monetag SDK <script> + any Monetag-created DOM artifacts
 * (overlays, iframes, popups). The script itself can't be un-executed, but
 * removing the tag plus the UI it mounts stops it from showing any new ads.
 */
function _removeSdk() {
  const tag = document.getElementById(SCRIPT_ID);
  if (tag) tag.remove();
  // Monetag injects iframes with ad-content; strip any whose src points at a
  // Monetag CDN host so a stale ad doesn't linger across the quiet period.
  try {
    const monetagHosts = /quge5|libtl|n6wxm|monetag|whephiwums|niphaum/i;
    document.querySelectorAll("iframe").forEach((f) => {
      if (f.src && monetagHosts.test(f.src)) f.remove();
    });
  } catch { /* defensive — never throw */ }
  _log("sdk_removed", { cycle: _cycleCount });
}

/**
 * Begin the window → quiet → window loop. Idempotent — calling twice is a
 * no-op. Caller must invoke stopAdFrequency() on route unmount to clean up.
 */
export function startAdFrequency() {
  if (_running || typeof window === "undefined") return;
  _running = true;
  _timers = [];
  _cycleCount = 0;
  _log("start", { grace_ms: GRACE_MS, window_ms: WINDOW_MS, quiet_ms: QUIET_MS });

  const runCycle = () => {
    if (!_running) return;
    if (_paused) {
      // Combat / boss fight in progress — defer this cycle by one quiet period
      // instead of injecting the SDK mid-fight.
      _timers.push(setTimeout(runCycle, QUIET_MS));
      return;
    }
    _cycleCount += 1;
    _injectSdk();
    _timers.push(setTimeout(() => {
      if (!_running) return;
      _removeSdk();
      _timers.push(setTimeout(runCycle, QUIET_MS));
    }, WINDOW_MS));
  };

  _timers.push(setTimeout(runCycle, GRACE_MS));
}

/**
 * Pause the ad cadence (e.g. during active combat / boss fights). While
 * paused, we immediately strip the SDK from the DOM so a vignette can't
 * fire mid-fight, and future cycles are deferred. Call `resumeAdFrequency()`
 * when the fight ends.
 */
export function pauseAdFrequency() {
  if (_paused) return;
  _paused = true;
  _removeSdk();
  _log("paused", { reason: "combat" });
}

/** Resume cycles after a combat pause. Does not change the overall schedule. */
export function resumeAdFrequency() {
  if (!_paused) return;
  _paused = false;
  _log("resumed", {});
}

/** Tear down all timers and remove the SDK. Safe to call unconditionally. */
export function stopAdFrequency() {
  if (!_running) return;
  _running = false;
  _paused = false;
  _timers.forEach((t) => clearTimeout(t));
  _timers = [];
  _removeSdk();
  _log("stop", {});
}

/** Diagnostic helper — surface current state in /debug/ads. */
export function getAdFrequencyStatus() {
  return {
    running: _running,
    paused: _paused,
    cycle: _cycleCount,
    sdk_in_dom: !!document.getElementById(SCRIPT_ID),
    config: { grace_ms: GRACE_MS, window_ms: WINDOW_MS, quiet_ms: QUIET_MS },
  };
}
